import { motion } from 'framer-motion';
import { Link } from '@/components/LocalizedLink';
import { useQuery } from '@tanstack/react-query';
import { useLanguage } from '@/contexts/LanguageContext';
import FloatingAsset from '@/components/FloatingAsset';
import SEOHead from '@/components/SEOHead';
import { fetchPublishedBlogPosts } from '@/lib/contentService';
import { getStaticBlogPosts } from '@/lib/blogStaticAdapter';
import { resolveLocalizedValue } from '@/lib/blogLocalization';
import { getBlogLogicalPath } from '@/lib/blogSlugs';
import { getBlogCoverImage } from '@/lib/blogCovers';
import BlogCardMedia from '@/components/blog/BlogCardMedia';
import assetDiamond from '@/assets/asset-chrome-diamond.webp';
import { SITE_URL } from '@/lib/seo';
import { buildBreadcrumbSchema, buildCollectionItemListSchema } from '@/lib/schema';

const BlogPage = () => {
  const { t, lang } = useLanguage();

  const { data } = useQuery({
    queryKey: ['blog', 'published'],
    queryFn: fetchPublishedBlogPosts,
    placeholderData: getStaticBlogPosts,
  });

  const posts = data ?? [];
  const pageTitle = lang === 'tr' ? 'Figtures Blog' : 'Figtures Blog';
  const pageDescription =
    lang === 'tr'
      ? 'AI, SaaS, ürün tasarımı ve yazılım mühendisliği üzerine uygulanabilir rehberler, teknik içgörüler ve saha deneyimlerinden çıkan dersler.'
      : 'Actionable guides, technical insights, and field lessons on AI, SaaS, product design, and software engineering.';

  const itemListSchema = buildCollectionItemListSchema({
    name: pageTitle,
    description: pageDescription,
    canonicalPath: '/blog',
    items: posts.map((post, index) => ({
      name: resolveLocalizedValue(post.title_i18n, post.title, lang),
      url: getBlogLogicalPath(post, lang),
      position: index + 1,
    })),
  });

  const breadcrumbSchema = buildBreadcrumbSchema([
    { name: lang === 'tr' ? 'Ana Sayfa' : 'Home', item: SITE_URL },
    { name: lang === 'tr' ? 'Blog' : 'Blog', item: `${SITE_URL}/blog` },
  ]);

  return (
    <main className="pt-32 pb-20 relative overflow-hidden">
      <SEOHead
        title={
          lang === 'tr'
            ? 'AI, SaaS ve Yazılım Geliştirme Blogu'
            : 'AI, SaaS & Software Engineering Blog'
        }
        description={pageDescription}
        canonical="/blog"
        jsonLd={[itemListSchema, breadcrumbSchema]}
      />
      <FloatingAsset src={assetDiamond} size={130} className="top-40 -right-6 opacity-35" floatDuration={10} />

      <div className="container mx-auto px-6 lg:px-12 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="mb-16 max-w-3xl"
        >
          <p className="font-body text-xs tracking-[0.3em] text-muted-foreground mb-4 uppercase">{t('blog.tag')}</p>
          <h1 className="font-display text-5xl lg:text-7xl font-bold text-foreground">{t('blog.title')}</h1>
          <p className="font-body text-lg text-muted-foreground mt-6 leading-relaxed">{t('blog.desc')}</p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {(data ?? []).map((post, i) => {
            const readingTime = resolveLocalizedValue(post.reading_time_i18n, '', lang);
            const title = resolveLocalizedValue(post.title_i18n, post.title, lang);
            const category = resolveLocalizedValue(post.category_i18n, post.category, lang);
            const excerpt = resolveLocalizedValue(post.excerpt_i18n, post.excerpt, lang);
            return (
              <Link key={post.id} to={getBlogLogicalPath(post, lang)}>
                <motion.article
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-40px' }}
                  transition={{ duration: 0.45, delay: (i % 3) * 0.06 }}
                  whileHover={{ y: -6, transition: { type: 'spring', stiffness: 300 } }}
                  className="group cursor-pointer"
                >
                  <BlogCardMedia image={getBlogCoverImage(post)} alt={title} category={category} priority={i < 3} />
                  <time className="font-body text-xs text-muted-foreground">
                    {new Date(post.published_at || post.created_at).toLocaleDateString(lang === 'tr' ? 'tr-TR' : 'en-US', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric'
                    })}
                  </time>
                  {readingTime ? (
                    <span className="font-body text-xs text-muted-foreground ml-2">• {readingTime}</span>
                  ) : null}
                  <h3 className="font-display text-lg font-semibold text-foreground mt-2 group-hover:text-foreground/70 transition-colors duration-300">
                    {title}
                  </h3>
                  <p className="font-body text-sm text-muted-foreground mt-2 leading-relaxed line-clamp-2">
                    {excerpt}
                  </p>
                </motion.article>
              </Link>
            );
          })}
        </div>
      </div>
    </main>
  );
};

export default BlogPage;
